import { useEffect, useMemo, useRef, useState } from "react";
import { X, Plus } from "lucide-react";

// Deterministic brand-color rotation per tag name (same helper as AdminClients.jsx)
const TAG_PALETTE = [
  { bg: "#4A0A66", fg: "#FFFFFF" }, // UN-GRAPE
  { bg: "#DF1283", fg: "#FFFFFF" }, // UN-BERRY
  { bg: "#0FFFE2", fg: "#000000" }, // UN-WATER
  { bg: "#FC861E", fg: "#FFFFFF" }, // UN-PUMPKIN
  { bg: "#D0FF0D", fg: "#000000" }, // UN-LIME
];
const tagColor = (name) => {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = ((h << 5) - h + name.charCodeAt(i)) | 0;
  return TAG_PALETTE[Math.abs(h) % TAG_PALETTE.length];
};

/**
 * Chip-based tag input with autocomplete + explicit "Create and apply" indicator.
 * Props:
 *   value: string[]           — currently selected tags
 *   onChange: (string[]) => void
 *   library: string[]         — known tag names for suggestions
 *   placeholder?: string
 *   testId?: string           — prefix for data-testids
 *   maxTags?: number          — default 20
 *   maxLen?: number           — default 30
 */
export function TagAutocomplete({
  value = [],
  onChange,
  library = [],
  placeholder = "Type a tag and press Enter…",
  testId = "tag-input",
  maxTags = 20,
  maxLen = 30,
}) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Click-outside closes dropdown
  useEffect(() => {
    const onClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const q = text.trim();
  const qLower = q.toLowerCase();
  const alreadySelected = (t) => value.some((v) => v.toLowerCase() === t.toLowerCase());

  const suggestions = useMemo(() => {
    if (!q) return library.filter((t) => !alreadySelected(t)).slice(0, 8);
    return library
      .filter((t) => t.toLowerCase().includes(qLower) && !alreadySelected(t))
      .slice(0, 8);
  }, [q, qLower, library, value]);

  const exactMatch = q && library.some((t) => t.toLowerCase() === qLower);
  const showCreate = q && !exactMatch && !alreadySelected(q);

  // Reset activeIdx when the option list changes
  useEffect(() => { setActiveIdx(0); }, [q, suggestions.length]);

  const optionCount = suggestions.length + (showCreate ? 1 : 0);

  const addTag = (raw) => {
    const t = (raw || "").trim().slice(0, maxLen);
    if (!t) return;
    if (alreadySelected(t)) { setText(""); return; }
    if (value.length >= maxTags) return;
    onChange([...value, t]);
    setText("");
    setActiveIdx(0);
  };
  const removeAt = (i) => onChange(value.filter((_, idx) => idx !== i));

  const commitActive = () => {
    if (!optionCount) { if (q) addTag(q); return; }
    if (activeIdx < suggestions.length) addTag(suggestions[activeIdx]);
    else if (showCreate) addTag(q);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" || e.key === "Tab" || e.key === ",") {
      if (q || optionCount) {
        e.preventDefault();
        commitActive();
      }
    } else if (e.key === "Backspace" && !text && value.length) {
      removeAt(value.length - 1);
    } else if (e.key === "ArrowDown" && optionCount) {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % optionCount);
      setOpen(true);
    } else if (e.key === "ArrowUp" && optionCount) {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + optionCount) % optionCount);
      setOpen(true);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div ref={wrapperRef} className="relative" data-testid={`${testId}-wrapper`}>
      <div
        className="flex flex-wrap items-center gap-1.5 rounded-lg border border-gray-300 px-2 py-1.5 focus-within:ring-2 focus-within:ring-[#0FFFE2] focus-within:border-[#0FFFE2] cursor-text bg-white"
        onClick={() => inputRef.current?.focus()}
        data-testid={`${testId}-chips`}
      >
        {value.map((t, i) => {
          const col = tagColor(t);
          return (
            <span
              key={`${t}-${i}`}
              data-testid={`${testId}-chip-${t}`}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold"
              style={{ background: col.bg, color: col.fg }}
            >
              #{t}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); removeAt(i); }}
                className="hover:bg-black/25 rounded-full p-0.5"
                aria-label={`Remove ${t}`}
                data-testid={`${testId}-chip-remove-${t}`}
              >
                <X size={10} strokeWidth={3} />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          value={text}
          onChange={(e) => { setText(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={value.length ? "" : placeholder}
          maxLength={maxLen}
          data-testid={`${testId}-input`}
          className="flex-1 min-w-[8rem] px-2 py-1 text-sm outline-none bg-transparent"
        />
      </div>

      {open && (suggestions.length > 0 || showCreate) && (
        <div
          className="absolute z-50 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg"
          data-testid={`${testId}-dropdown`}
        >
          {suggestions.map((s, i) => {
            const col = tagColor(s);
            const active = i === activeIdx;
            return (
              <button
                key={s}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); addTag(s); }}
                onMouseEnter={() => setActiveIdx(i)}
                data-testid={`${testId}-suggest-${s}`}
                className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors ${active ? "bg-gray-50" : "bg-white"} hover:bg-gray-50`}
              >
                <span
                  className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold"
                  style={{ background: col.bg, color: col.fg }}
                >
                  #{s}
                </span>
                <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400">Apply</span>
              </button>
            );
          })}
          {showCreate && (
            <button
              type="button"
              onMouseDown={(e) => { e.preventDefault(); addTag(q); }}
              onMouseEnter={() => setActiveIdx(suggestions.length)}
              data-testid={`${testId}-create`}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm border-t border-gray-100 transition-colors ${activeIdx === suggestions.length ? "bg-[#D0FF0D]/30" : "bg-white"} hover:bg-[#D0FF0D]/30`}
            >
              <span className="inline-flex items-center gap-1.5 text-[12px] font-bold text-[#000000]">
                <Plus size={12} strokeWidth={3} />
                Create and apply <span className="text-[#4A0A66]">#{q}</span>
              </span>
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-gray-400">New</span>
            </button>
          )}
        </div>
      )}

      {value.length >= maxTags && (
        <div className="mt-1 text-[10px] text-[#DF1283] font-bold">Maximum {maxTags} tags reached.</div>
      )}
    </div>
  );
}

export default TagAutocomplete;
